import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import {Box, Stack, Checkbox, InputAdornment, TextField, Typography, List, ListItem, Avatar, ListItemAvatar, ListItemText, createTheme, ThemeProvider, Autocomplete, Chip, IconButton, Skeleton, CircularProgress, Button } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useSnackbar } from 'notistack';
import type { CasinoCategory, CasinoGame, CasinoGamesFilter } from '../../../Casino/Type/CasinoTypes';
import { cleanProviderName, getCategoryIconUrl, getGameThumbUrl, getProviderIconUrl } from '../../../Casino/CasinoHelper';
import { useCasinoProviders, useCasinoCategories, useCasinoGames, createProvidersMap, createCategoriesMap } from '../../../Casino/CasinoQueries';
import { CasinoLobbyPreset, consumeCasinoLobbyPreset, subscribeCasinoLobbyPreset } from '../../../Casino/CasinoLobbyPreset';
import { preloadImages, toImageSrc } from '../../../Utils/ImagePreload';
import { useCasinoLinkAction } from '../../../Casino/CasinoHelper';
import CasinoGamesVirtualList from './CasinoGamesVirtualList';
import styles from '../../../assets/css/components/CasinoLobby.module.css';
import MobileRoomBar from '../../RoomBar/MobileRoomBar';
import MobileHeader from '../../Header/MobileHeader';
import { IUserState } from '../../../Core/State/IUserState';
import { GameCard } from './CasinoGameCard';

interface CasinoLobbyProps {
  userState: IUserState;
  section: string;
  logout: () => void;
  userConnectedCount: number;
}

const PAGE_SIZE = 36; // Smaller batches for smoother loading
const UI_CACHE_TTL_MS = 10 * 60_000; // Keep UI-only cache briefly between unmount/remount
const THUMB_PRELOAD_COUNT_DESKTOP = 24;
const THUMB_PRELOAD_COUNT_MOBILE = 12;
const THUMB_PRELOAD_CONCURRENCY = 4;

const shouldPreloadThumbs = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  const connection = (navigator as { connection?: { saveData?: boolean; effectiveType?: string } }).connection;
  if (!connection) return true;
  if (connection.saveData) return false;
  const effectiveType = connection.effectiveType;
  return effectiveType !== 'slow-2g' && effectiveType !== '2g';
};

// Lightweight cache for UI state only (scroll position, filter selections)
type CasinoLobbyUICache = {
  isMobile: boolean;
  savedAt: number;
  searchInput: string;
  selectedProviderIds: string[];
  selectedCategoryIds: string[];
  scrollOffset: number;
};

let casinoLobbyUICache: CasinoLobbyUICache | null = null;

const CasinoLobby: React.FC<CasinoLobbyProps> = ({ userState, section, logout, userConnectedCount }) => {
  const { t } = useTranslation('Casino');
  const { enqueueSnackbar } = useSnackbar();
  const executeLinkAction = useCasinoLinkAction();
  const SCROLL_TOP_THRESHOLD_PX = isMobile ? 300 : 600;

  const initialLobbyPreset = useMemo(() => consumeCasinoLobbyPreset(), []);

  // Restore UI state from lightweight cache (filters, scroll position only)
  const restorableCache =
    !initialLobbyPreset &&
    casinoLobbyUICache &&
    casinoLobbyUICache.isMobile === isMobile &&
    Date.now() - casinoLobbyUICache.savedAt < UI_CACHE_TTL_MS
      ? casinoLobbyUICache
      : null;

  const scrollOffsetRef = useRef<number>(restorableCache?.scrollOffset ?? 0);

  // Filter state
  const [searchInput, setSearchInput] = useState<string>(
    () => initialLobbyPreset?.searchInput ?? restorableCache?.searchInput ?? ''
  );
  const [searchTitle, setSearchTitle] = useState<string>(() => {
    if (initialLobbyPreset?.searchInput !== undefined) return initialLobbyPreset.searchInput.trim();
    return (restorableCache?.searchInput ?? '').trim();
  });
  const [selectedProviderIds, setSelectedProviderIds] = useState<string[]>(
    () => initialLobbyPreset?.providerIds ?? restorableCache?.selectedProviderIds ?? []
  );
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>(
    () => initialLobbyPreset?.categoryIds ?? restorableCache?.selectedCategoryIds ?? []
  );
  const [providerFilterText, setProviderFilterText] = useState<string>('');
  const [categoryFilterText, setCategoryFilterText] = useState<string>('');

  const [launchingGameId, setLaunchingGameId] = useState<string | null>(null);
  const [scrollToTopTrigger, setScrollToTopTrigger] = useState<number>(0);
  const [showScrollTopButton, setShowScrollTopButton] = useState<boolean>(
    () => (restorableCache?.scrollOffset ?? 0) > SCROLL_TOP_THRESHOLD_PX
  );

  // Compute filter key for scroll state management
  const listKey = useMemo(
    () => `${searchTitle}|${selectedProviderIds.join(',')}|${selectedCategoryIds.join(',')}`,
    [searchTitle, selectedProviderIds, selectedCategoryIds]
  );
  const previousListKeyRef = useRef<string>(listKey);
  const listKeyChanged = previousListKeyRef.current !== listKey;
  const initialScrollOffset = listKeyChanged ? 0 : scrollOffsetRef.current;
  const lastThumbPreloadKeyRef = useRef<string | null>(null);

  // Keep scroll state keyed to filters.
  useEffect(() => {
    if (listKeyChanged) {
      scrollOffsetRef.current = 0;
    }
    previousListKeyRef.current = listKey;
  }, [listKey, listKeyChanged]);

  // Persist UI state on unmount
  useEffect(() => {
    return () => {
      casinoLobbyUICache = {
        isMobile,
        savedAt: Date.now(),
        searchInput,
        selectedProviderIds,
        selectedCategoryIds,
        scrollOffset: scrollOffsetRef.current,
      };
    };
  }, [searchInput, selectedProviderIds, selectedCategoryIds]);

  // Debounce search to avoid spamming requests while typing
  useEffect(() => {
    const t = setTimeout(() => {
      setSearchTitle(searchInput.trim());
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // === React Query Hooks ===

  // Fetch providers with automatic caching and deduplication
  const {
    data: providersData,
    isLoading: providersLoading,
    error: providersError,
  } = useCasinoProviders(true);

  // Fetch categories with automatic caching and deduplication
  const {
    data: categoriesData,
    isLoading: categoriesLoading,
    error: categoriesError,
  } = useCasinoCategories();

  // Sort and memoize providers/categories
  const providers = useMemo(() => {
    if (!providersData) return [];
    return [...providersData].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
  }, [providersData]);

  const categories = useMemo(() => {
    if (!categoriesData) return [];
    return [...categoriesData].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
  }, [categoriesData]);

  const metaLoading = providersLoading || categoriesLoading;

  // Create lookup maps
  const providersById = useMemo(() => createProvidersMap(providers), [providers]);
  const categoriesById = useMemo(() => createCategoriesMap(categories), [categories]);

  // Preload icons when providers/categories load
  useEffect(() => {
    const iconUrls: string[] = [];
    providers.forEach((p) => {
      const url = getProviderIconUrl(p);
      if (url) iconUrls.push(url);
    });
    categories.forEach((c) => {
      const url = getCategoryIconUrl(c);
      if (url) iconUrls.push(url);
    });
    void preloadImages(iconUrls);
  }, [providers, categories]);

  // Show error toasts for metadata loading failures
  useEffect(() => {
    if (providersError) {
      enqueueSnackbar(providersError.message || t('errors.loadProvidersFailed'), { variant: 'error' });
    }
  }, [providersError, enqueueSnackbar]);

  useEffect(() => {
    if (categoriesError) {
      enqueueSnackbar(categoriesError.message || t('errors.loadCategoriesFailed'), { variant: 'error' });
    }
  }, [categoriesError, enqueueSnackbar]);

  // Build games filter
  const gamesFilter = useMemo<CasinoGamesFilter>(() => {
    const filter: CasinoGamesFilter = {
      status: 'Active',
      mainProvider: 'slotegrator',
      isMobile,
    };
    if (searchTitle.length > 0) filter.searchTitle = searchTitle;
    if (selectedCategoryIds.length > 0) filter.categoryIds = selectedCategoryIds;
    if (selectedProviderIds.length > 0) filter.providerIds = selectedProviderIds;
    return filter;
  }, [searchTitle, selectedCategoryIds, selectedProviderIds, isMobile]);

  // Fetch games with infinite scroll support
  const {
    allGames: games,
    total,
    hasMore,
    isLoading: gamesLoading,
    isFetchingNextPage: loadingMore,
    fetchNextPage,
    error: gamesError,
  } = useCasinoGames(gamesFilter, PAGE_SIZE);

  // Show error toast for games loading failure
  useEffect(() => {
    if (gamesError) {
      enqueueSnackbar(gamesError.message || t('errors.loadGamesFailed'), { variant: 'error' });
    }
  }, [gamesError, enqueueSnackbar]);

  useEffect(() => {
    if (gamesLoading || games.length === 0) return;
    if (!shouldPreloadThumbs()) return;
    if (lastThumbPreloadKeyRef.current === listKey) return;

    lastThumbPreloadKeyRef.current = listKey;
    const preloadCount = isMobile ? THUMB_PRELOAD_COUNT_MOBILE : THUMB_PRELOAD_COUNT_DESKTOP;
    const urls = games
      .slice(0, preloadCount)
      .map((game) => toImageSrc(getGameThumbUrl(game)))
      .filter((url): url is string => Boolean(url));

    void preloadImages(urls, { concurrency: THUMB_PRELOAD_CONCURRENCY });
  }, [games, gamesLoading, isMobile, listKey]);

  // Reset scroll UI when filters change
  useEffect(() => {
    setShowScrollTopButton(false);
  }, [listKey]);

  const {
    allGames: tagFilteredGames,
    isLoading: tagFilteredLoading,
  } = useCasinoGames({
    status: 'Active',
    tagSlugs: ['top-of-the-charts'],
    isMobile,
  }, 20);

  const requestNextPage = useCallback(() => {
    if (!hasMore || gamesLoading || loadingMore) return;
    fetchNextPage();
  }, [hasMore, gamesLoading, loadingMore, fetchNextPage]);

  const handleScrollOffsetChange = useCallback((scrollOffset: number) => {
    scrollOffsetRef.current = scrollOffset;
    const nextShow = scrollOffset > SCROLL_TOP_THRESHOLD_PX;
    setShowScrollTopButton((prev) => (prev === nextShow ? prev : nextShow));
  }, [SCROLL_TOP_THRESHOLD_PX]);

  const handleTaggedGamesWheel = useCallback((event: React.WheelEvent<HTMLDivElement>) => {
    const container = event.currentTarget;
    const maxScrollLeft = container.scrollWidth - container.clientWidth;
    if (maxScrollLeft <= 0) return;

    const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
    if (delta === 0) return;

    const isAtStart = container.scrollLeft <= 0;
    const isAtEnd = container.scrollLeft >= maxScrollLeft - 1;
    if ((delta < 0 && isAtStart) || (delta > 0 && isAtEnd)) return;

    event.preventDefault();
    container.scrollBy({ left: delta, behavior: 'smooth' });
  }, []);

  const scrollToTop = useCallback(() => {
    scrollOffsetRef.current = 0;
    setShowScrollTopButton(false);
    setScrollToTopTrigger((prev) => prev + 1);
  }, []);

  const applyLobbyPreset = useCallback((preset: CasinoLobbyPreset) => {
    // Apply preset filters - React Query will automatically refetch
    setSearchInput(preset.searchInput);
    setSearchTitle(preset.searchInput.trim());
    setSelectedProviderIds(preset.providerIds);
    setSelectedCategoryIds(preset.categoryIds);

    // Reset scroll position
    scrollOffsetRef.current = 0;
    setShowScrollTopButton(false);
    setScrollToTopTrigger((prev) => prev + 1);
  }, []);

  useEffect(() => {
    return subscribeCasinoLobbyPreset((preset) => {
      const consumed = consumeCasinoLobbyPreset();
      applyLobbyPreset(consumed || preset);
    });
  }, [applyLobbyPreset]);

  const launchGame = useCallback(
    async (game: CasinoGame, demo: boolean) => {
      if (!game.slug) return;

      setLaunchingGameId(game.id);

      try {
        await executeLinkAction({
          action: {
            type: 'game',
            slug: game.slug,
            title: game.title,
            image: getGameThumbUrl(game),
            providerId: game.providerId,
          },
          demoOverride: demo,
          game,
        });
      } catch (err: any) {
        enqueueSnackbar(err?.message || t('errors.launchFailed'), { variant: 'error' });
      } finally {
        setLaunchingGameId(null);
      }
    },
    [enqueueSnackbar, executeLinkAction]
  );

  // Sort options with selected first
  const sortedProviders = useMemo(() => {
    return [...providers].sort((a, b) => {
      const aSelected = selectedProviderIds.includes(a.id);
      const bSelected = selectedProviderIds.includes(b.id);
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;
      return (a.position ?? 0) - (b.position ?? 0);
    });
  }, [providers, selectedProviderIds]);

  // Filter providers based on filter text
  const filteredProviders = useMemo(() => {
    if (!providerFilterText.trim()) return sortedProviders;
    const filterLower = providerFilterText.toLowerCase();
    return sortedProviders.filter((p) =>
      cleanProviderName(p.title).toLowerCase().includes(filterLower)
    );
  }, [sortedProviders, providerFilterText]);

  const sortedCategories = useMemo(() => {
    return [...categories].sort((a, b) => {
      const aSelected = selectedCategoryIds.includes(a.id);
      const bSelected = selectedCategoryIds.includes(b.id);
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;
      return (a.position ?? 0) - (b.position ?? 0);
    });
  }, [categories, selectedCategoryIds]);

  // Filter categories based on filter text
  const filteredCategories = useMemo(() => {
    if (!categoryFilterText.trim()) return sortedCategories;
    const filterLower = categoryFilterText.toLowerCase();
    return sortedCategories.filter((c) =>
      c.title.toLowerCase().includes(filterLower)
    );
  }, [sortedCategories, categoryFilterText]);

  // Check if any filters are active
  const hasActiveFilters = searchInput.length > 0 || selectedProviderIds.length > 0 || selectedCategoryIds.length > 0;

  // Reset all filters
  const resetFilters = () => {
    setSearchInput('');
    setSelectedProviderIds([]);
    setSelectedCategoryIds([]);
    setProviderFilterText('');
    setCategoryFilterText('');
  };

  const handleProviderToggle = (providerId: string) => {
    setSelectedProviderIds((prev) =>
      prev.includes(providerId) ? prev.filter((id) => id !== providerId) : [...prev, providerId]
    );
  };

  const handleCategoryToggle = (categoryId: string) => {
    setSelectedCategoryIds((prev) =>
      prev.includes(categoryId) ? prev.filter((id) => id !== categoryId) : [...prev, categoryId]
    );
  };

  const darkTheme = createTheme({
    palette: {
      mode: 'dark',
      primary: {
        main: '#F93751',
      },
    },
    typography: {
      fontFamily: 'Inter, serif',
    },
  });

  return (
    <ThemeProvider theme={darkTheme}>
      <Stack className={isMobile ? styles.casinoLobbyMobile : styles.casinoLobby} direction={ isMobile ? 'column' : 'row'} spacing={isMobile ? 0 : 2}>
        {isMobile && (
          <>
            <MobileHeader 
              userState={userState}
              section={section}
              logout={logout}
            />
            <MobileRoomBar
                userConnectedCount={userConnectedCount}
            />
          </>
        )}
        <Stack className={isMobile ? styles.casinoLobbyContentMobile : styles.casinoLobbyContent} style={{ order: isMobile ? 2 : 'initial' }}>
          {!hasActiveFilters && (
            <>
              {tagFilteredLoading ? (
                <Box sx={{ mb: 2 }}>
                  <Skeleton><Typography style={{ fontSize: '14px', fontWeight: 600, color: '#FFFFFF', marginBottom: '0.5rem' }}>{t('lobby.topOfTheCharts')}</Typography></Skeleton>
                  <Stack
                    direction="row"
                    spacing={1}
                    className={styles.taggedGamesContainer}
                    onWheel={handleTaggedGamesWheel}
                  >
                    {Array.from({ length: 12 }).map((_, index) => (
                      <Box key={index} className={isMobile ? styles.gameCardMobile : styles.gameCard}>
                        <Box sx={{ width: '100%', height: 'auto', aspectRatio: '17 / 19', mb: 0.5 }}><Skeleton variant='rounded' height="100%" /></Box>
                        <Stack direction="row" alignItems="center" gap={1} sx={{ width: '100%' }}>
                          <Skeleton variant="rounded" width={14} height={14} />
                          <Skeleton variant="text" width={60} sx={{ fontSize: '10px' }} />
                          <Skeleton variant="circular" width={16} height={16} sx={{ ml: 'auto' }} />
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              ) : (
                <Box sx={{ mb: 2 }}>
                  <Typography style={{ fontSize: '14px', fontWeight: 600, color: '#FFFFFF', marginBottom: '0.5rem' }}>{t('lobby.topOfTheCharts')}</Typography>
                  <Stack
                    direction="row"
                    spacing={1}
                    className={isMobile ? styles.taggedGamesContainerMobile : styles.taggedGamesContainer}
                    onWheel={handleTaggedGamesWheel}
                  >
                    {tagFilteredGames.map((game) => {
                      const provider = game?.providerId ? providersById.get(game.providerId) : undefined;
                      const providerName = provider?.title ? cleanProviderName(provider.title) : null;
                      const providerIconUrl = getProviderIconUrl(provider);
                      const gameCategories = (game?.categoryIds || [])
                        .map((categoryId) => categoriesById.get(categoryId))
                        .filter((category): category is CasinoCategory => Boolean(category));
                      return (
                        <GameCard
                          key={game.id}
                          game={game}
                          providerName={providerName}
                          providerIconUrl={providerIconUrl}
                          gameCategories={gameCategories}
                          launchingGameId={launchingGameId}
                          onLaunch={launchGame}
                          taggedGame={true}
                        />
                      )}
                    )}
                  </Stack>
                </Box>
              )}
            </>
          )}
          <Typography style={{ fontSize: '14px', fontWeight: 600, color: '#FFFFFF', marginBottom: '0.5rem' }}>{t('lobby.allGames')}</Typography>
          {gamesLoading ? (
            <Stack direction="row" spacing={1} className={isMobile ? styles.loadingContainerMobile : styles.loadingContainer}>
              {Array.from({ length: 20 }).map((_, index) => (
                <Box key={index} className={isMobile ? styles.gameCardMobile : styles.gameCard}>
                  <Box sx={{ width: '100%', height: 'auto', aspectRatio: '287 / 193', mb: 0.5 }}><Skeleton variant='rounded' height="100%" /></Box>
                  <Stack direction="row" alignItems="center" gap={1} sx={{ width: '100%' }}>
                    <Skeleton variant="rounded" width={14} height={14} />
                    <Skeleton variant="text" width={60} sx={{ fontSize: '10px' }} />
                    <Skeleton variant="circular" width={16} height={16} sx={{ ml: 'auto' }} />
                  </Stack>
                </Box>
              ))}
            </Stack>
          ) : games.length === 0 ? (
            <Box className={styles.emptyState}>
              <Box className={styles.emptyIcon}>
                <SportsEsportsIcon sx={{ color: '#9A9AA0', fontSize: 48 }} />
              </Box>
              <Typography variant="h6" className={styles.emptyText} sx={{ color: '#9A9AA0', mb: 1 }}>{t('lobby.noGamesFound')}</Typography>
              <Button variant="contained" size="small"className={styles.emptyButton} onClick={resetFilters}>
                {t('lobby.clearFilters')}
              </Button>
            </Box>
          ) : (
            <Box className={styles.casinoGamesListViewport}>
              <CasinoGamesVirtualList
                key={listKey}
                games={games}
                total={total}
                providersById={providersById}
                categoriesById={categoriesById}
                launchingGameId={launchingGameId}
                loadingMore={loadingMore}
                onLaunch={launchGame}
                onLoadMore={requestNextPage}
                initialScrollOffset={initialScrollOffset}
                onScrollOffsetChange={handleScrollOffsetChange}
                scrollToTopTrigger={scrollToTopTrigger}
              />
              {showScrollTopButton && (
                <IconButton
                  aria-label={t('lobby.scrollToTop', 'Scroll to top')}
                  className={styles.casinoScrollTopButton}
                  onClick={scrollToTop}
                >
                  <KeyboardArrowUpIcon />
                </IconButton>
              )}
            </Box>
          )}
        </Stack>
        {isMobile ? (
          <Stack className={styles.casinoLobbyMobileSidebar}>
            <TextField
              variant="outlined"
              size="small"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={t('lobby.searchGames')}
              sx={{
                mx: '10px',
                mt: '10px',
                '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                backgroundColor: '#22242c',
                '& .MuiInputBase-input::placeholder': {
                  color: '#8695a6',
                  opacity: 1,
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#8695a6', fontSize: 18 }} />
                  </InputAdornment>
                ),
                endAdornment: searchInput ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setSearchInput('')}
                      sx={{ color: '#8695a6', padding: '4px' }}
                    >
                      <CloseIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
                className: styles.searchInput,
              }}
            />
            <Stack style={{ order: isMobile ? 1 : 'initial' }} direction='row' spacing={1} px="10px" pt="10px">
              <Autocomplete
                multiple
                disablePortal
                disableCloseOnSelect
                limitTags={2}
                size="small"
                id="provider-autocomplete"
                options={sortedProviders}
                getOptionLabel={(o) => cleanProviderName(o.title)}
                value={providers.filter((p) => selectedProviderIds.includes(p.id))}
                onChange={(_, value) => setSelectedProviderIds(value.map((v) => v.id))}
                className={styles.autocomplete}
                slotProps={{
                  paper: {
                    sx: {
                      minWidth: '60vw',
                      maxWidth: '70vw',
                    },
                  },
                }}
                renderTags={(tagValue, getTagProps) =>
                  tagValue.map((option, index) => {
                    const { key } = getTagProps({ index });
                    return (
                      <img key={key} src={toImageSrc(getProviderIconUrl(option))} alt={cleanProviderName(option.title)} width='auto' height={16} style={{ marginLeft: '4px' }} />
                    );
                  })
                }
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label={t('lobby.providers')}
                    variant="outlined"
                    className={styles.autocompleteTextField}
                  />
                )}
                renderOption={(props, option, { selected }) => {
                  const { key, ...optionProps } = props;
                  const iconSrc = toImageSrc(getProviderIconUrl(option));
                  return (
                    <Box key={key} component="li" {...optionProps}>
                      <Avatar sx={{ bgcolor: '#1c1c23', width: 32, height: 32, mr: 1 }}>
                        {iconSrc ? (
                          <img src={iconSrc} alt={cleanProviderName(option.title)} height={16} width='auto' />
                        ) : 
                          cleanProviderName(option.title)
                        }
                      </Avatar>
                      {cleanProviderName(option.title)}
                      <Checkbox
                        icon={<CheckBoxOutlineBlankIcon sx={{ fontSize: 18 }} />}
                        checkedIcon={<CheckBoxIcon sx={{ fontSize: 18 }} />}
                        style={{ marginLeft: 'auto' }}
                        checked={selected}
                      />
                    </Box>
                  );
                }}
              />
              <Autocomplete
                multiple
                disablePortal
                disableCloseOnSelect
                limitTags={2}
                size="small"
                id="category-autocomplete"
                options={sortedCategories}
                getOptionLabel={(o) => o.title}
                loading={metaLoading}
                value={categories.filter((c) => selectedCategoryIds.includes(c.id))}
                onChange={(_, value) => setSelectedCategoryIds(value.map((v) => v.id))}
                className={styles.autocomplete}
                renderTags={(tagValue, getTagProps) =>
                  tagValue.map((option, index) => {
                    const { key } = getTagProps({ index });
                    return (
                      <img key={key} src={toImageSrc(getCategoryIconUrl(option))} alt={option.title} width='auto' height={16} style={{ marginLeft: '4px' }} />
                    );
                  })
                }
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label={t('lobby.categories')}
                    variant="outlined"
                    className={styles.autocompleteTextField}
                  />
                )}
                renderOption={(props, option, { selected }) => {
                  const { key, ...optionProps } = props;
                  const iconSrc = toImageSrc(getCategoryIconUrl(option));
                  return (
                    <Box key={key} component="li" {...optionProps} style={{ textTransform: 'capitalize' }}>
                      <Avatar sx={{ bgcolor: '#1c1c23', width: 32, height: 32, mr: 1 }}>
                        {iconSrc ? (
                          <img src={iconSrc} alt={option.title} height={16} width='auto' />
                        ) : 
                          option.title
                        }
                      </Avatar>
                      {option.title}
                      <Checkbox
                        icon={<CheckBoxOutlineBlankIcon sx={{ fontSize: 18 }} />}
                        checkedIcon={<CheckBoxIcon sx={{ fontSize: 18 }} />}
                        style={{ marginLeft: 'auto' }}
                        checked={selected}
                      />
                    </Box>
                  );
                }}
              />
            </Stack>
          </Stack>
        ) : (
          <Stack className={styles.casinoLobbySidebar}>
            <Box className={styles.searchContainer}>
              <TextField
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder={t('lobby.searchGames')}
                size="small"
                className={`${styles.searchField} ${isMobile ? styles.searchFieldMobile : ''}`}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#5a5a65', fontSize: 18 }} />
                    </InputAdornment>
                  ),
                  className: styles.searchInput,
                }}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                }}
              />
            </Box>
            <Box className={styles.sidebarLabel} px={1} py={0.25} mt={2} mb={1}>
                <Typography style={{ fontSize: '10px', fontWeight: '600', color: '#FFF0E3' }}>{t('lobby.providers')}</Typography>
            </Box>
            <Box className={styles.listContainer}>
              <List sx={{ py: 0, height: '100%' }} dense className={styles.customScrollbar}>
                {filteredProviders.map((provider) => {
                  const isSelected = selectedProviderIds.includes(provider.id);
                  const iconSrc = toImageSrc(getProviderIconUrl(provider));
  
                  return (
                    <ListItem
                      key={provider.id}
                      onClick={() => handleProviderToggle(provider.id)}
                      sx={{
                      }}
                    >
                      <ListItemAvatar sx={{ minWidth: 0, mr: 1 }}>
                        <Avatar sx={{ bgcolor: '#1c1c23', width: 32, height: 32 }}>
                          {iconSrc ? (
                            <img src={iconSrc} alt={cleanProviderName(provider.title)} />
                          ) : 
                            cleanProviderName(provider.title)
                          }
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText primary={cleanProviderName(provider.title)} />
                      <Checkbox
                        icon={<CheckBoxOutlineBlankIcon sx={{ fontSize: 18 }} />}
                        checkedIcon={<CheckBoxIcon sx={{ fontSize: 18 }} />}
                        checked={isSelected}
                      />
                    </ListItem>
                  );
                })}
              </List>
            </Box>
            <Box className={styles.sidebarLabel} px={1} py={0.25} mt={2} mb={1}>
                <Typography style={{ fontSize: '10px', fontWeight: '600', color: '#FFF0E3' }}>{t('lobby.categories')}</Typography>
            </Box>
            <Box className={styles.listContainer}>
              <List sx={{ py: 0, height: '100%' }} dense className={styles.customScrollbar}>
                {filteredCategories.map((category) => {
                  const isSelected = selectedCategoryIds.includes(category.id);
                  const iconSrc = toImageSrc(getCategoryIconUrl(category));
  
                  return (
                    <ListItem
                      key={category.id}
                      onClick={() => handleCategoryToggle(category.id)}
                      sx={{
                      }}
                    >
                      <ListItemAvatar sx={{ minWidth: 0, mr: 1 }}>
                        <Avatar sx={{ bgcolor: '#1c1c23', width: 32, height: 32 }}>
                          {iconSrc ? (
                            <img src={iconSrc} alt={category.title} style={{ maxWidth: '50%', maxHeight: '50%' }} />
                          ) : 
                            category.title
                          }
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText primary={category.title} sx={{ textTransform: 'capitalize' }} />
                      <Checkbox
                        icon={<CheckBoxOutlineBlankIcon sx={{ fontSize: 18 }} />}
                        checkedIcon={<CheckBoxIcon sx={{ fontSize: 18 }} />}
                        checked={isSelected}
                        // sx={{ p: 0, mr: 1, color: '#666', '&.Mui-checked': { color: '#F52740' } }}
                      />
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          </Stack>
        )}
      </Stack>
    </ThemeProvider>
  );
  // return (
  //   <Box className={`${styles.casinoLobby} ${isMobile ? styles.casinoLobbyMobile : ''}`}>
  //     {/* Filters */}
  //     <Box className={`${styles.filters} ${isMobile ? styles.filtersMobile : ''}`}>
  //       {hasActiveFilters && (
  //         <Button
  //           size="small"
  //           onClick={resetFilters}
  //           startIcon={<FilterAltOffIcon sx={{ fontSize: 16 }} />}
  //           sx={{
  //             color: '#8695A6',
  //             fontSize: '12px',
  //             textTransform: 'none',
  //             whiteSpace: 'nowrap',
  //             minWidth: 'auto',
  //             px: 1.5,
  //             '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.05)' },
  //           }}
  //         >
  //           Reset
  //         </Button>
  //       )}

  //       {/* Examples: link to game/provider/category (dev-only) */}
  //       {import.meta.env.DEV && (
  //         <Box sx={{ display: 'flex', gap: 1, ...(isMobile ? { flexWrap: 'wrap' } : { ml: 'auto' }) }}>
  //           <CasinoLinkButton
  //             size="small"
  //             variant="outlined"
  //             demo
  //             action={{
  //               type: 'game',
  //               slug: exampleGameSlug,
  //               title: exampleGameBySlug?.title || 'Flame Busters',
  //               image: exampleGameBySlug ? getGameThumbUrl(exampleGameBySlug) : null,
  //               providerId: exampleGameBySlug?.providerId,
  //             }}
  //             sx={{
  //               textTransform: 'none',
  //               fontSize: '12px',
  //               borderColor: 'rgba(255,255,255,0.18)',
  //               color: 'rgba(255,255,255,0.9)',
  //               '&:hover': { borderColor: 'rgba(245,39,64,0.8)', bgcolor: 'rgba(245,39,64,0.08)' },
  //             }}
  //           >
  //             Example game
  //           </CasinoLinkButton>
  //           <CasinoLinkButton
  //             size="small"
  //             variant="outlined"
  //             disabled={!exampleProvider?.slug}
  //             action={{ type: 'provider', slug: exampleProvider?.slug }}
  //             sx={{
  //               textTransform: 'none',
  //               fontSize: '12px',
  //               borderColor: 'rgba(255,255,255,0.18)',
  //               color: 'rgba(255,255,255,0.9)',
  //               '&:hover': { borderColor: 'rgba(245,39,64,0.8)', bgcolor: 'rgba(245,39,64,0.08)' },
  //             }}
  //           >
  //             Example provider
  //           </CasinoLinkButton>
  //           <CasinoLinkButton
  //             size="small"
  //             variant="outlined"
  //             disabled={!exampleCategory?.slug}
  //             action={{ type: 'category', slug: exampleCategory?.slug }}
  //             sx={{
  //               textTransform: 'none',
  //               fontSize: '12px',
  //               borderColor: 'rgba(255,255,255,0.18)',
  //               color: 'rgba(255,255,255,0.9)',
  //               '&:hover': { borderColor: 'rgba(245,39,64,0.8)', bgcolor: 'rgba(245,39,64,0.08)' },
  //             }}
  //           >
  //             Example category
  //           </CasinoLinkButton>
  //         </Box>
  //       )}
  //     </Box>

  //     {/* Games Grid */}
  //     <Box sx={{ flex: 1, minHeight: 0, position: 'relative' }}>
  //       {gamesLoading ? (
  //         <Box className={styles.loadingContainer}>
  //           <CircularProgress size={28} sx={{ color: '#F52740' }} />
  //           <Typography className={styles.loadingText}>Loading...</Typography>
  //         </Box>
  //       ) : games.length === 0 ? (
  //         <Box className={styles.emptyState}>
  //           <Box className={styles.emptyIcon}>
  //             <SportsEsportsIcon sx={{ color: '#666', fontSize: 32 }} />
  //           </Box>
  //           <Typography className={styles.emptyText}>No games found</Typography>
  //           <Typography className={styles.emptySubtext}>
  //             Try different filters
  //           </Typography>
  //         </Box>
  //       ) : (
  //         <>
  //           <CasinoGamesVirtualList
  //             key={listKey}
  //             games={games}
  //             total={total}
  //             providersById={providersById}
  //             categoriesById={categoriesById}
  //             launchingGameId={launchingGameId}
  //             loadingMore={loadingMore}
  //             onLaunch={launchGame}
  //             onLoadMore={requestNextPage}
  //             initialScrollOffset={initialScrollOffset}
  //             onScrollOffsetChange={handleScrollOffsetChange}
  //             scrollToTopTrigger={scrollToTopTrigger}
  //           />
  //           {showScrollTopButton && (
  //             <IconButton
  //               aria-label="Scroll to top"
  //               onClick={scrollToTop}
  //               sx={{
  //                 position: 'absolute',
  //                 right: 16,
  //                 bottom: 16,
  //                 zIndex: 5,
  //                 width: 42,
  //                 height: 42,
  //                 borderRadius: '10px',
  //                 bgcolor: 'rgba(30,31,37,0.92)',
  //                 border: '1px solid rgba(255,255,255,0.12)',
  //                 color: 'rgba(255,255,255,0.9)',
  //                 boxShadow: '0 10px 26px rgba(0,0,0,0.55)',
  //                 backdropFilter: 'blur(6px)',
  //                 transition: 'transform 150ms ease, background 150ms ease, border-color 150ms ease',
  //                 '&:hover': {
  //                   bgcolor: 'rgba(245,39,64,0.92)',
  //                   borderColor: 'rgba(245,39,64,0.65)',
  //                   transform: 'translateY(-2px)',
  //                 },
  //                 '&:active': { transform: 'translateY(0)' },
  //               }}
  //             >
  //               <KeyboardArrowUpIcon sx={{ fontSize: 22 }} />
  //             </IconButton>
  //           )}
  //         </>
  //       )}
  //     </Box>
  //   </Box>
  // );
};

export default CasinoLobby;
